/**
 * Features Card Component
 * 
 * Displays key features overview in chat.
 */

import React from 'react';
import { Box, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import SecurityIcon from '@mui/icons-material/Security';
import SpeedIcon from '@mui/icons-material/Speed';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import AutoModeIcon from '@mui/icons-material/AutoMode';

interface Feature {
  icon: React.ReactNode;
  title: string;
  description: string;
}

const features: Feature[] = [
  {
    icon: <SmartToyIcon />,
    title: 'AI-Powered Messages',
    description: 'Generate personalized messages that sound like you wrote them',
  },
  {
    icon: <PersonSearchIcon />,
    title: 'Smart Targeting',
    description: 'Find the right people with advanced search and filters',
  },
  {
    icon: <AutoModeIcon />,
    title: 'Automated Sequences',
    description: 'Multi-step campaigns that run while you sleep',
  },
  {
    icon: <SecurityIcon />,
    title: 'Account Protection',
    description: 'Human-like behavior and conservative limits keep you safe',
  },
  {
    icon: <AnalyticsIcon />,
    title: 'Detailed Analytics',
    description: 'Track performance and optimize your outreach',
  },
  {
    icon: <SpeedIcon />,
    title: 'Quick Setup',
    description: '15 minutes from signup to your first campaign',
  },
];

export const FeaturesCard: React.FC = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  return (
    <Box className={classes.card}>
      <Typography variant="h6" className={classes.title}>
        {t('publicChat.featuresTitle', 'What SAIA Can Do For You')}
      </Typography>

      <Box className={classes.featuresGrid}>
        {features.map((feature, index) => (
          <Box key={index} className={classes.feature}>
            <Box className={classes.iconWrapper}>
              {feature.icon}
            </Box>
            <Box>
              <Typography variant="subtitle2" className={classes.featureTitle}>
                {feature.title}
              </Typography>
              <Typography variant="body2" className={classes.featureDescription}>
                {feature.description}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const useStyles = makeStyles()((theme) => ({
  card: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: 16,
    border: `1px solid ${theme.palette.greys.lighter}`,
    padding: theme.spacing(3),
    maxWidth: 600,
    boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08)',
  },
  title: {
    color: theme.palette.text.primary,
    fontWeight: 600,
    marginBottom: theme.spacing(3),
  },
  featuresGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
    },
  },
  feature: {
    display: 'flex',
    gap: theme.spacing(2),
    padding: theme.spacing(1.5),
    borderRadius: 10,
    backgroundColor: theme.palette.backgrounds.main_new,
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: `${theme.palette.primary.main}0A`,
    },
  },
  iconWrapper: {
    width: 40,
    height: 40,
    borderRadius: 10,
    background: `linear-gradient(135deg, ${theme.palette.primary.main}33 0%, ${theme.palette.secondary.main}33 100%)`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    '& svg': {
      fontSize: 20,
      color: theme.palette.primary.main,
    },
  },
  featureTitle: {
    color: theme.palette.text.primary,
    fontWeight: 600,
    marginBottom: theme.spacing(0.25),
  },
  featureDescription: {
    color: theme.palette.text.secondary,
    fontSize: '0.8rem',
    lineHeight: 1.4,
  },
}));

export default FeaturesCard;

